import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ItemId from 'owa-service/lib/contract/ItemId';
import {
    SubstrateSearchTextSuggestionAttributes,
    SubstrateSearchSuggestionAttributes,
    SubstrateSearchSuggestionSegment,
} from './SubstrateSearchSuggestionsResponse';
import { PrivateDistributionListMember } from 'owa-persona';

export enum SuggestionKind {
    Keywords,
    People,
    PrivateDistributionList,
    Files,
    Category,
    Message,
    Event,
}

export type SuggestionSource =
    | 'none' // Used for PeopleSuggestions objects that are created for certain non-pure search scenarios, such as persona fallback search
    | 'gal' // GAL are the people suggestions that are not part of the recipient cache but are present in user's global contact directory
    | 'localCache' // These are the suggestions present in the user's recipient cache for people kind
    | 'qf'; // These are the suggestions received from QF source

export interface KeywordSuggestion {
    kind: SuggestionKind.Keywords;
    DisplayText: string;
    QueryText: string;
    ReferenceId: string;
    Attributes: SubstrateSearchTextSuggestionAttributes;
    Segments?: SubstrateSearchSuggestionSegment[];
}

export interface CategorySearchSuggestion {
    kind: SuggestionKind.Category;
    Name: string;
    ReferenceId: string;
}

interface PeopleSuggestionBase {
    Attributes: SubstrateSearchSuggestionAttributes;
    DisplayName: string;
    HighlightedDisplayName: string;
    ReferenceId: string;
    Source: SuggestionSource;
    QueryText?: string;
    CustomQueryText?: {
        FromKql?: string;
        ToKql?: string;
    };
}

export interface PeopleSuggestion extends PeopleSuggestionBase {
    kind: SuggestionKind.People;
    EmailAddresses: string[];
    MailboxType?: string;
}

export interface PrivateDistributionListSuggestion extends PeopleSuggestionBase {
    kind: SuggestionKind.PrivateDistributionList;
    PdlId: string;
    OwsPersonaId: string;
    Members: PrivateDistributionListMember[];
}

export interface MessageSuggestion {
    kind: SuggestionKind.Message;
    Subject: string;
    DisplayName: string;
    ReferenceId: string;
    ItemId: ItemId;
    ConversationId: ItemId;
    DateTimeReceived: string;
    HasAttachments: boolean;
}

export interface FileSuggestion {
    kind: SuggestionKind.Files;
    Name: string;
    Sender: string;
    Extension: string;
    ItemId: string;
    ConversationId: string;
    AttachmentId: string;
    Subject: string;
    Attachment: AttachmentType;
    ReferenceId: string;
    HighlightedDisplayText: string;
    DateTimeReceived: string;
}

export interface EventSuggestion {
    kind: SuggestionKind.Event;
    Subject: string;
    Text: string;
    Start: string;
    End: string;
    EventId: string;
    ReferenceId: string;
    Location: string;
    OrganizerName: string;
    OrganizerAddress: string;
}

/**
 * Suggestions which are shown in a rich pill form in the search box
 */
export type PillSuggestion =
    | PeopleSuggestion
    | PrivateDistributionListSuggestion
    | CategorySearchSuggestion
    | FileSuggestion;

export type Suggestion =
    | KeywordSuggestion
    | PeopleSuggestion
    | PrivateDistributionListSuggestion
    | MessageSuggestion
    | FileSuggestion
    | CategorySearchSuggestion
    | EventSuggestion;

interface SuggestionSet {
    Suggestions: Suggestion[];
    IsComplete: boolean;
    TraceId?: string;
}

export default SuggestionSet;
